import React, { useEffect, useState } from 'react';
import { History, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { cn } from '../../lib/utils';
import api from '../../lib/api';
import { fmtOrg, tzSuffix } from '../../lib/orgTime';
import { useOrgTz } from '../../hooks/useOrgTz';

// Quarter-by-quarter timeline of every version of a step's leader note.
export function NoteTimeline({ stepKey, label, orgId, cohortId, versions, open, onOpenChange }) {
  const tz = useOrgTz();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!open) return;
    setRows(null);
    const q = new URLSearchParams(); if (orgId) q.set('organization_id', orgId); if (cohortId) q.set('cohort_id', cohortId);
    api.get(`/story-guide/notes/${stepKey}/history${q.toString() ? `?${q}` : ''}`).then((r) => setRows(r.data.versions || [])).catch(() => setRows([]));
  }, [open, stepKey, orgId, cohortId, versions]);
  const quarters = rows ? [...new Set(rows.map((v) => v.quarter))] : [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={`story-note-${stepKey}-timeline`}>
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4 text-indigo-600" /> {label} · note timeline</DialogTitle><DialogDescription>How the leader's narrative on this step evolved, quarter by quarter. Newest first.</DialogDescription></DialogHeader>
        {!rows ? <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : !rows.length ? <p className="text-sm text-slate-500 py-4" data-testid={`story-note-${stepKey}-timeline-empty`}>No notes have been written on this step yet.</p> : (
          <ol className="relative ml-2 border-l-2 border-indigo-100 space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            {quarters.map((qtr) => (
              <li key={qtr} className="pl-4">
                <span className="absolute -left-[9px] mt-1 h-4 w-4 rounded-full bg-indigo-600 ring-4 ring-white" />
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-indigo-700" data-testid={`story-note-${stepKey}-timeline-quarter-${qtr}`}>{qtr}</p>
                <div className="mt-1.5 space-y-2">
                  {rows.filter((v) => v.quarter === qtr).map((v, i) => (
                    <div key={`${v.saved_at}-${i}`} className={cn('rounded-lg border px-3 py-2', v.current ? 'border-indigo-300 bg-indigo-50/60' : 'border-slate-200 bg-white')} data-testid={`story-note-${stepKey}-timeline-version`} data-current={v.current ? 'true' : 'false'}>
                      <p className="text-sm italic text-slate-800 leading-snug">“{v.text}”</p>
                      <p className="mt-1 text-[11px] text-slate-500">— {v.author || 'Leader'} · {v.saved_at ? `${fmtOrg(v.saved_at, tz)}${tzSuffix(tz)}` : ''}{v.current ? <span className="ml-2 rounded bg-indigo-600 px-1.5 py-0.5 text-[10px] font-bold text-white not-italic">Current</span> : v.removed ? <span className="ml-2 text-rose-600 font-semibold">removed {v.replaced_by ? `by ${v.replaced_by}` : ''}</span> : <span className="ml-2">revised {v.replaced_by ? `by ${v.replaced_by}` : ''}</span>}</p>
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}
